



































import { computed, defineComponent, PropType, ref } from '@vue/composition-api';
import { mdiPencilOutline } from '@mdi/js';
import { useContext } from '@berlin-front/common/composables/context';
import { ThemeContext } from '@berlin-front/common/services/ThemeContext';
import PatchedVTextField from '@berlin-front/common/components/elements/PatchedVTextField';
import { bemx } from '@berlin-front/common/utils';

/**
 * ## メールアドレスフィールド
 *
 * メールアドレスのテキストフィールドを扱うコンポーネント。
 * 見た目はVuetify標準のOutlinedをベースに、色とラベルの一部をCSSで上書きしている。
 * heightは標準で56px、`dense`を指定すると低くなり40pxとなる。
 *
 * 値の入力がある場合は表示モードで初期表示し、編集アイコンをクリックすると編集モードになる。
 * 値の入力が無い場合、または、初期編集可能の設定が有効な場合は最初から編集モードとなる。
 * 一度編集モードにした場合、画面操作では表示モードには戻せない。
 *
 * バリデーションの指定は親コンポーネントで実施する。
 * エラーメッセージを親から受け取り、本コンポーネントは親側へeventの伝搬のみを行う。
 *
 * Vuetifyの`v-text-field`については以下を参照。
 *
 * - 仕様書：https://vuetifyjs.com/ja/api/v-text-field/
 * - コード：https://github.com/vuetifyjs/vuetify/tree/master/packages/vuetify/src/components/VTextField
 *
 */
export default defineComponent({
  name: 'AppInputMail',
  components: {
    PatchedVTextField,
  },

  props: {
    /** エラーメッセージ。
     * バリデーションエラー時に表示するメッセージ。
     * バリデーションオブジェクトの状態によって変化する。
     * */
    errorMessages: {
      type: Array as PropType<string[] | unknown[]>,
      default: null,
    },

    /** 初期編集可能。
     * 値が入っている場合でも、表示モードでなく最初から編集モードで表示する。 */
    initEditable: {
      type: Boolean,
      default: false,
    },

    /** ラベル。
     * テキストフィールドの項目ラベルとして使用される。
     * 正式なフィールド名でなく、簡易的な名称をラベルに設定しても問題なし。
     * また、テキストフィールドによってはラベル名の記載がないものもある。
     * */
    label: {
      type: String,
      default: '',
    },

    /** プレースホルダー。 必要に応じて入力例を設定する。 */
    placeholder: {
      type: String,
      default: '',
    },

    /** testId */
    dataTestid: {
      type: String,
      default: '',
    },

    /** 入力値。 v-modelで指定した値が連動される。 */
    value: {
      type: String as PropType<string>,
      default: null,
    },

    /** 編集不可モード */
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    /**
     * inputイベント
     */
    'input',
    /**
     * blurイベント
     * @type {Event}
     */
    'blur',
    /**
     * 変更イベント
     */
    'change',
    /**
     * IME入力終了イベント
     */
    'compositionend',
  ],

  setup(props, { emit }) {
    // v-modelと連動
    const inputValue = computed({
      get: () => props.value,
      set: (val) => emit('input', val),
    });
    // 値がある場合は表示モード、ない場合または初期編集可は編集モードで初期表示
    const editing = ref(!props.value || props.initEditable);
    // 編集アイコン押下で編集モードに（表示モードには戻れない）
    const textEdit = () => {
      editing.value = true;
    };

    const blurInput = (event: Event) => emit('blur', event);
    const onChange = (event: Event) => emit('change', event);
    const onCompositionEnd = (event: Event) => emit('compositionend', event);

    const { themeParameter } = useContext(ThemeContext);
    const primaryColor = computed(() => themeParameter.value.primaryColor);

    const className = bemx('AppInputMail');

    return {
      blurInput,
      onChange,
      onCompositionEnd,
      className,
      editing,
      inputValue,
      mdiPencilOutline,
      primaryColor,
      textEdit,
    };
  },
});
